/**
 * Payload и сравнение для ИИ DeepSeek в ВПР «Ручное управление».
 */

function manualRowHasData(row) {
  if (!Array.isArray(row)) return false;
  return row.some((c) => String(c ?? '').trim() !== '');
}

function readCrossCell(row, colIdx, part, headers, fileConfig, addedColumns) {
  const c = parseInt(colIdx, 10);
  if (!Number.isFinite(c) || c < 0) return '';
  if (part === 'fio' || part === 'phone') {
    const pair = fileConfig?._splitColIndices;
    const orig = fileConfig?._splitColOriginalIndex;
    let phys = c;
    if (pair && orig != null && c === orig) {
      phys = part === 'phone' ? pair[1] : pair[0];
    }
    return row[phys] != null ? String(row[phys]).trim() : '';
  }
  return row[c] != null ? String(row[c]).trim() : '';
}

/**
 * Собрать данные из загруженных слотов для /api/vpr-deepseek-cross-match.
 */
export function buildVprDeepseekCrossMatchPayload(
  loadedIndices,
  manualFiles,
  manualCrossFileMatch,
  manualFileConfigs,
  manualRenames,
  manualMainFileIndex = 0,
) {
  const matchMode = manualCrossFileMatch.matchMode === 'phone_fio'
    ? 'phone_fio'
    : manualCrossFileMatch.matchMode === 'phone_nd'
      ? 'phone_nd'
      : manualCrossFileMatch.matchMode === 'leading_phone_fio'
        ? 'leading_phone_fio'
        : 'phone';
  const files = loadedIndices.map((slotIdx) => {
    const entry = manualFiles[slotIdx];
    const p = manualCrossFileMatch.phoneBySlot[slotIdx] || {};
    const f = manualCrossFileMatch.fioBySlot[slotIdx] || {};
    const nd = manualCrossFileMatch.ndBySlot?.[slotIdx] || {};
    const sheetName = (entry?.excelSheetName || '').trim();
    const baseName = String(entry?.fileName || '').trim() || `Файл ${slotIdx + 1}`;
    const label = sheetName ? `${baseName} → ${sheetName}` : baseName;
    const cfg = manualFileConfigs[slotIdx] || {};
    return {
      label,
      slotIndex: slotIdx,
      headers: (entry?.headers || []).map((h, i) => (manualRenames[slotIdx] || {})[i] ?? h ?? ''),
      rows: (entry?.rows || []).filter(manualRowHasData),
      crossPhoneColIndex: p.col != null && p.col !== '' ? p.col : '',
      crossPhoneColPart: p.part || '',
      crossFioColIndex: f.col != null && f.col !== '' ? f.col : '',
      crossFioColPart: f.part || '',
      crossNdColIndex: nd.col != null && nd.col !== '' ? nd.col : '',
      exportDate: (manualCrossFileMatch.exportDateBySlot[slotIdx] || '').trim(),
      includeInFullMatch: manualCrossFileMatch.includeInFullMatchBySlot[slotIdx] !== false,
      _cfg: cfg,
    };
  });

  const mainMapped = loadedIndices.indexOf(manualMainFileIndex);
  return {
    matchMode,
    noCallRule: manualCrossFileMatch.noCallRule || 'auto',
    leadingFileIndex: mainMapped >= 0 ? mainMapped : 0,
    leadingOnlyNd: !!manualCrossFileMatch.leadingOnlyNd,
    files: files.map(({ _cfg, ...rest }) => rest),
  };
}

export function parseEngineResultFromManualBlob(sheets) {
  const findSheet = (pred) => (sheets || []).find((s) => pred(String(s.name || '')));
  const noCallSh = findSheet((n) => n.includes('дозвонились') || n.includes('не звонить'));
  const callSh = findSheet((n) => n.includes('перезвонить') || (n.includes('звонить') && !n.includes('не')));
  const leadMatchSh = findSheet((n) => n.includes('совпадения с ведущим'));
  const leadOnlySh = findSheet((n) => n.includes('только в ведущем'));
  const parseList = (sh, phoneCol = 0, fioCol = 1) => {
    if (!sh?.rows?.length) return [];
    return sh.rows.map((row) => ({
      phone: String(row[phoneCol] ?? '').trim(),
      fio: String(row[fioCol] ?? '').trim(),
      files: [],
      key: String(row[phoneCol] ?? '').trim(),
    })).filter((r) => r.phone);
  };
  return {
    noCall: parseList(noCallSh),
    callOnly: parseList(callSh),
    foundElsewhere: parseList(leadMatchSh),
    leadingOnly: parseList(leadOnlySh),
    fromExcel: !!(noCallSh || callSh || leadMatchSh || leadOnlySh),
  };
}

export const VPR_DEEPSEEK_API_KEY_STORAGE = 'vpr_deepseek_api_key_v1';

export const VPR_DEEPSEEK_API_TIMEOUT_MS = 130000;

function rowKey(r) {
  return String(r?.key || r?.phone || '').trim();
}

function filesStr(r) {
  return Array.isArray(r?.files) ? r.files.join('; ') : '';
}

/** Листы для вкладок в модалке «Результат» (после анализа ИИ). */
export function buildDeepseekResultSheets(deepseekResult) {
  if (!deepseekResult?.engine) return [];

  const eng = deepseekResult.engine;
  const ai = deepseekResult.ai;
  const isNd = eng.matchMode === 'phone_nd';
  const isLeading = eng.matchMode === 'leading_phone_fio';
  const catNoCall = isNd ? 'дозвонились' : 'не звонить';
  const catCall = isNd ? 'перезвонить (НД)' : isLeading ? 'только в ведущем' : 'звонить';
  const catMatch = isLeading ? 'совпадения с ведущим' : null;
  const sheets = [];

  const byKey = new Map();
  const ingest = (list, field, category) => {
    (list || []).forEach((r) => {
      const k = rowKey(r);
      if (!k) return;
      if (!byKey.has(k)) {
        byKey.set(k, { phone: r.phone || '', fio: r.fio || '', key: k });
      }
      const o = byKey.get(k);
      o[field] = category;
      o[`${field}Files`] = filesStr(r);
    });
  };

  ingest(eng.noCall, 'engineCat', catNoCall);
  ingest(eng.callOnly, 'engineCat', catCall);
  if (isLeading && catMatch) {
    ingest(eng.foundElsewhere || eng.inMultiple, 'engineCat', catMatch);
  }
  if (ai?.ok) {
    ingest(ai.noCall, 'aiCat', catNoCall);
    ingest(ai.callOnly, 'aiCat', catCall);
    if (isLeading) {
      ingest(ai.foundElsewhere, 'aiCat', catMatch);
      ingest(ai.leadingOnly, 'aiCat', catCall);
    }
  }

  const cmpRows = [...byKey.values()]
    .sort((a, b) => String(a.phone).localeCompare(String(b.phone), 'uk'))
    .map((o) => {
      const e = o.engineCat || '—';
      const aCat = o.aiCat || '—';
      let status = 'совпало';
      if (e !== aCat) status = 'расхождение';
      if (e === '—' && aCat === '—') status = '—';
      else if (e !== '—' && aCat === '—') status = 'только движок';
      else if (e === '—' && aCat !== '—') status = 'только ИИ';
      return [
        o.phone,
        o.fio,
        e,
        aCat,
        status,
        o.engineCatFiles || '',
        o.aiCatFiles || '',
      ];
    });

  if (cmpRows.length) {
    sheets.push({
      name: 'Сравнение ИИ/движок',
      headers: ['Телефон', 'ФИО', 'Движок', 'ИИ', 'Статус', 'Файлы (движок)', 'Файлы (ИИ)'],
      rows: cmpRows,
      _deepseek: true,
    });
  }

  if (ai?.ok) {
    sheets.push({
      name: isNd ? 'ИИ — не звонить (дозвонились)' : 'ИИ — не звонить',
      headers: ['Телефон', 'ФИО', 'Файлы', 'Причина'],
      rows: (ai.noCall || []).map((r) => [r.phone, r.fio, filesStr(r), r.reason || '']),
      _deepseek: true,
    });
    sheets.push({
      name: isNd ? 'ИИ — перезвонить (НД)' : 'ИИ — звонить',
      headers: ['Телефон', 'ФИО', 'Файлы', 'Причина'],
      rows: (ai.callOnly || []).map((r) => [r.phone, r.fio, filesStr(r), r.reason || '']),
      _deepseek: true,
    });
  }

  if (isLeading) {
    sheets.push({
      name: 'Движок — совпадения с ведущим',
      headers: ['Телефон', 'ФИО', 'Другие файлы', 'Статус'],
      rows: (eng.foundElsewhere || eng.inMultiple || []).map((r) => [
        r.phone,
        r.fio,
        Array.isArray(r.otherFiles) ? r.otherFiles.join('; ') : filesStr(r),
        r.status || '',
      ]),
      _deepseek: true,
    });
    sheets.push({
      name: 'Движок — только в ведущем',
      headers: ['Телефон', 'ФИО', 'Статус'],
      rows: (eng.leadingOnly || eng.callOnly || []).map((r) => [r.phone, r.fio, r.status || '']),
      _deepseek: true,
    });
  } else {
    sheets.push({
      name: isNd ? 'Движок (сверка) — дозвонились' : 'Движок (сверка) — не звонить',
      headers: ['Телефон', 'ФИО', 'Файлы', 'Статус'],
      rows: (eng.noCall || []).map((r) => [r.phone, r.fio, filesStr(r), r.status || '']),
      _deepseek: true,
    });
    sheets.push({
      name: isNd ? 'Движок (сверка) — перезвонить (НД)' : 'Движок (сверка) — звонить',
      headers: ['Телефон', 'ФИО', 'Файлы', 'Статус'],
      rows: (eng.callOnly || []).map((r) => [r.phone, r.fio, filesStr(r), r.status || '']),
      _deepseek: true,
    });
  }

  return sheets;
}

export function getDeepseekComparisonSummary(deepseekResult) {
  const cmp = deepseekResult?.comparison;
  if (!cmp?.available) return null;
  return {
    noCall: cmp.noCall,
    callOnly: cmp.callOnly,
    aiOk: !!deepseekResult?.ai?.ok,
    notes: deepseekResult?.ai?.notes || '',
  };
}
